import { useEffect, useId, useState } from 'react';
import { useApp, useCloudAuth, useCloudSummary, useCloudSync, useSession, useUi, useWindow } from '../app/context';
import { openAccount } from '../app/ui';
import type { CloudSummary } from '../cloud/summary';
import { relativeTime } from '../cloud/summary';
import type { CloudDocState } from '../core/cloud';
import type { DocMeta } from '../core/documents';
import { Popover } from './Popover';
import { CLOUD_LINES } from './DocumentCard';

/**
 * The cloud, in the head: one small glyph that is almost nothing while all is
 * well. It changes shape rather than shouting — a sweep while words travel, a
 * slash when offline, two bars when autosave is paused, a warm point when
 * something needs the writer — and opens a small card on request.
 *
 * Honesty rules, from the engine: "synced" appears only when the summary says
 * so (no pending, no failed document); a short-lived upload does not flicker
 * the glyph (the working state shows only after a beat), and the arrival at
 * "synced" is marked once with a brief check, never before it is true.
 */

export type SyncLook = 'synced' | 'working' | 'pending' | 'paused' | 'offline' | 'attention' | 'conflict';

const CONFLICT = / \(conflict copy [^)]*\)(\.[^.]*)?$/;

export function isConflictCopy(name: string): boolean {
  return CONFLICT.test(name);
}

export function lookOf(summary: CloudSummary, autosave: boolean, conflicts: number): SyncLook {
  if (conflicts > 0 && summary.tone !== 'attention') return 'conflict';
  switch (summary.state) {
    case 'synced':
      return 'synced';
    case 'syncing':
    case 'loading':
      return 'working';
    case 'pending':
      return autosave ? 'pending' : 'paused';
    case 'offline':
      return 'offline';
    default:
      return summary.tone === 'attention' ? 'attention' : 'working';
  }
}

const WORDS: Record<SyncLook, string> = {
  synced: 'Synced',
  working: 'Syncing',
  pending: 'Waiting to sync',
  paused: 'Cloud Autosave paused',
  offline: 'Offline',
  attention: 'Cloud needs attention',
  conflict: 'Conflict copy open',
};

/** Hold the "working" look back for a beat, so a quick upload doesn't blink; everything else shows at once. */
function useSteadyLook(look: SyncLook): { look: SyncLook; arrived: boolean } {
  const [shown, setShown] = useState(look);
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    if (look === shown) return;
    const delay = look === 'working' || look === 'pending' ? 450 : 0;
    const t = setTimeout(() => {
      setArrived(look === 'synced' && (shown === 'working' || shown === 'pending'));
      setShown(look);
    }, delay);
    return () => clearTimeout(t);
  }, [look, shown]);
  useEffect(() => {
    if (!arrived) return;
    const t = setTimeout(() => setArrived(false), 1400);
    return () => clearTimeout(t);
  }, [arrived]);
  return { look: shown, arrived };
}

export function SyncGlyph({ look, arrived = false, size = 18 }: { look: SyncLook; arrived?: boolean; size?: number }) {
  return (
    <svg className="sync-glyph" data-look={look} data-arrived={arrived || undefined} width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path className="sg-cloud" d="M6.2 15.4a3.6 3.6 0 0 1-.5-7.17 4.5 4.5 0 0 1 8.7 1.02 3.1 3.1 0 0 1-.42 6.15z" pathLength={100} />
      <path className="sg-sweep" d="M6.2 15.4a3.6 3.6 0 0 1-.5-7.17 4.5 4.5 0 0 1 8.7 1.02 3.1 3.1 0 0 1-.42 6.15z" pathLength={100} />
      <path className="sg-check" d="m7.6 11.4 1.7 1.6 3.2-3.4" pathLength={10} />
      <path className="sg-slash" d="M4 4.2 16.2 16.4" />
      <path className="sg-pause" d="M8.7 10v2.4M11.3 10v2.4" />
      <circle className="sg-point" cx="15.6" cy="14.6" r="2.3" />
    </svg>
  );
}

/** Live state of the cloud for the head. Renders nothing while signed out (local-only MyTXT stays untouched). */
export function SyncIndicator() {
  const app = useApp();
  const auth = useCloudAuth();
  const summary = useCloudSummary();
  const autosave = useUi((s) => s.prefs.cloudAutosave);
  const conflicts = useSession((s) => s.docs.filter((d) => d.cloud && isConflictCopy(d.name)).length);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const raw = lookOf(summary, autosave, conflicts);
  const { look, arrived } = useSteadyLook(raw);
  const visible = app.cloud.configured && (auth.status === 'signed-in' || auth.status === 'recovery' || summary.state === 'expired');
  if (!visible) return null;
  const label = look === 'synced' ? summary.label : `${WORDS[look]} — ${summary.label}`;
  return (
    <>
      <button
        ref={setAnchor}
        type="button"
        className="icon-btn sync-btn"
        data-look={look}
        aria-label={`Cloud: ${label}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-tip={open ? undefined : WORDS[look]}
        data-tip-side="bottom"
        onClick={() => setOpen(!open)}
      >
        <SyncGlyph look={look} arrived={arrived} />
      </button>
      <Popover open={open} anchor={anchor} placement="below-end" onClose={() => setOpen(false)} label="Cloud" className="sync-popover">
        <SyncCard look={raw} onDone={() => setOpen(false)} />
      </Popover>
    </>
  );
}

function SyncCard({ look, onDone }: { look: SyncLook; onDone: () => void }) {
  const app = useApp();
  const auth = useCloudAuth();
  const sync = useCloudSync();
  const summary = useCloudSummary();
  const autosave = useUi((s) => s.prefs.cloudAutosave);
  const activeId = useWindow((w) => w.activeId);
  const active = useSession((s) => s.docs.find((d) => d.id === activeId) ?? null);
  const docs = useSession((s) => s.docs);
  const copies = docs.filter((d) => d.cloud && isConflictCopy(d.name));
  const [busy, setBusy] = useState(false);
  const [moved, setMoved] = useState<string | null>(null);
  const switchId = useId();
  const signedIn = auth.status === 'signed-in';
  const granted = signedIn && !!sync && sync.access !== 'denied';
  const needsSignIn = !signedIn || sync?.blocked === 'auth';
  const [now] = useState(() => Date.now());

  const run = async (fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const moveActive = (doc: DocMeta) =>
    run(async () => {
      const [report] = await app.cloud.moveToCloud([doc.id]);
      setMoved(report?.detail ?? null);
    });

  return (
    <div className="sync-card" data-look={look}>
      <div className="sync-card-head" role="status" aria-live="polite">
        <span className="sync-card-glyph">
          <SyncGlyph look={look} size={22} />
        </span>
        <span className="sync-card-text">
          <strong>{summary.label}</strong>
          {(summary.detail || (summary.state === 'synced' && sync?.lastSyncedAt)) && (
            <small>{summary.detail ?? (sync?.lastSyncedAt ? `Last synced ${relativeTime(sync.lastSyncedAt, now)}` : '')}</small>
          )}
        </span>
      </div>

      {active && granted && <ActiveDocLine doc={active} state={active.cloud ? (sync?.docs[active.id] ?? 'pending') : null} busy={busy} moved={moved} onMove={() => moveActive(active)} />}

      {copies.length > 0 && (
        <div className="sync-card-copies">
          <p>A conflict copy keeps the words this device had when another device changed the same document. Compare, keep what you need, then close it.</p>
          {copies.slice(0, 3).map((d) => (
            <button
              key={d.id}
              type="button"
              className="sync-row-btn"
              onClick={() => {
                onDone();
                app.activate(d.id);
              }}
            >
              <span className="sync-row-name">{d.name}</span>
              <span className="sync-row-go">Show</span>
            </button>
          ))}
        </div>
      )}

      {granted && (
        <label className="acct-switch sync-card-switch" htmlFor={switchId}>
          <span className="acct-switch-text">
            <span>Cloud Autosave</span>
            <small>{autosave ? 'Uploads shortly after you pause.' : 'Paused — changes wait for Sync Now.'}</small>
          </span>
          <input id={switchId} type="checkbox" role="switch" className="acct-toggle" checked={autosave} onChange={(e) => app.setPrefs({ cloudAutosave: e.target.checked })} />
        </label>
      )}

      <div className="sync-card-actions">
        {needsSignIn ? (
          <button
            type="button"
            className="acct-btn"
            data-tone="primary"
            onClick={() => {
              onDone();
              openAccount();
            }}
          >
            Sign In Again
          </button>
        ) : (
          <button type="button" className="acct-btn" aria-disabled={busy || !sync || undefined} onClick={() => sync && run(() => app.cloud.syncNow())}>
            {busy ? 'Syncing…' : 'Sync Now'}
          </button>
        )}
        <button
          type="button"
          className="acct-btn"
          data-tone="quiet"
          onClick={() => {
            onDone();
            openAccount();
          }}
        >
          Library &amp; Account
        </button>
      </div>
    </div>
  );
}

function ActiveDocLine({ doc, state, busy, moved, onMove }: { doc: DocMeta; state: CloudDocState | null; busy: boolean; moved: string | null; onMove: () => void }) {
  if (state) {
    return (
      <div className="sync-card-doc" data-cloud={state}>
        <span className="sync-card-doc-name">{doc.name}</span>
        <small>{CLOUD_LINES[state]}</small>
      </div>
    );
  }
  return (
    <div className="sync-card-doc" data-cloud="local">
      <span className="sync-card-doc-name">{doc.name}</span>
      <small>{moved ?? 'On this device only. It isn’t uploaded unless you choose to.'}</small>
      {!moved && (
        <button type="button" className="acct-link" aria-disabled={busy || undefined} onClick={onMove}>
          {busy ? 'Moving…' : 'Move to Cloud'}
        </button>
      )}
    </div>
  );
}
